import PageTitle from '@/components/Admin/PageTitle';
import RadioBtn from '@/components/Admin/UI/RadioBtn';
import Select from '@/components/Admin/UI/Select';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { TableStore } from './Stores';
import { fetchStores, updateStore, uploadImageSlider } from '@/api/store';
import { useQuery } from '@tanstack/react-query';
import { useUIStore } from '@/store/useUIStore';
import { css } from 'styled-components';
import ColorSelect from '@/components/Admin/UI/ColorSelect';
import ShopTheme from '@/components/Admin/UI/ShopTheme';
import apiClient from '@/api/_axios';
import { toast } from 'react-toastify';
import useSaveChangesStore from '@/store/useSaveChangesStore';
import { SelectOption } from '@/types/shared';
import Badge from '@/components/Admin/UI/Badge';

interface Props {
   sample: string;
}

interface ApiError {
   response?: {
      status: number;
      data: {
         error: string;
      };
   };
   message?: string;
}

interface CustomizationValues {
   store: string;
   storeDomain: string;
   storename: string;
   location: string;
   settings: {
      btnColor: string;
      btnLabel: string;
      btnEmbed: string;
      designTheme: string;
      colorTheme: string;
      imgs: string[];
      slidesEnabled: boolean;
      slidesHeight: number;
   };
}

const Customization = () => {
   const {
      handleShowSave,
      handleSave,
      handleValues,
      successSave,
      handleSuccessSave,
   } = useSaveChangesStore();
   const { refresh, handleRefresh } = useUIStore();
   const initialValuesRef = useRef<CustomizationValues | null>(null);

   const {
      data: storesResponse,
      isLoading,
      error,
   } = useQuery({
      queryKey: ['store', refresh],
      queryFn: fetchStores,
   });

   const [hasChanges, setHasChanges] = useState(false);
   const [isUploading, setIsUploading] = useState(false);
   const [uploadProgress, setUploadProgress] = useState<number>(0);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [storeData, setStoreData] = useState<SelectOption[]>([]);

   const [values, setValues] = useState<CustomizationValues>({
      store: '',
      storeDomain: '',
      storename: '',
      location: '',
      settings: {
         btnColor: '#B3D946',
         btnLabel: 'Pay via Gcash',
         btnEmbed: '',
         designTheme: 'modern',
         colorTheme: '#B3D946',
         imgs: [],
         slidesEnabled: true,
         slidesHeight: 200,
      },
   });

   useEffect(() => {
      if (!storesResponse?.result) return;

      if (values.store.length === 0 && storesResponse.result[0]) {
         const newValues: CustomizationValues = {
            store: storesResponse.result[0]._id || '',
            storeDomain: storesResponse.result[0].storeDomain || '',
            storename: storesResponse.result[0].storename || '',
            location: storesResponse.result[0].location || '',
            settings: {
               btnColor:
                  storesResponse.result[0].settings?.btnColor || '#B3D946',
               btnLabel:
                  storesResponse.result[0].settings?.btnLabel ||
                  'Pay via Gcash',
               btnEmbed: storesResponse.result[0].settings?.btnEmbed || '',
               designTheme:
                  storesResponse.result[0].settings?.designTheme || 'modern',
               colorTheme:
                  storesResponse.result[0].settings?.colorTheme || '#B3D946',
               imgs: storesResponse.result[0].settings?.imgs || [],
               slidesEnabled: storesResponse.result[0].settings?.slidesEnabled !== false,
               slidesHeight: storesResponse.result[0].settings?.slidesHeight || 200,
            },
         };
         // console.log(`----`, newValues);

         const storeData = [
            ...storesResponse.result.map((item) => {
               return {
                  _id: item._id,
                  name: item.storename,
                  srcIcon: '',
               };
            }),
         ];
         setStoreData(storeData);

         // setValues(newValues);
         setValues({
            ...newValues,
            store: storeData[0]._id,
         });
         initialValuesRef.current = newValues;
      }
   }, [storesResponse, refresh]);

   // console.log(values);

   useEffect(() => {
      if (!storesResponse?.result || !values?.store) return;

      const getStore = storesResponse.result.find(
         (store: TableStore) => store._id === values.store
      );

      if (!getStore) return;

      const newValues: CustomizationValues = {
         store: values.store,
         storeDomain: getStore.storeDomain || '',
         storename: getStore.storename || '',
         location: getStore.location || '',
         settings: {
            btnColor: getStore.settings?.btnColor || '#B3D946',
            btnLabel: getStore.settings?.btnLabel || 'Pay via Gcash',
            btnEmbed: getStore.settings?.btnEmbed || '',
            designTheme: getStore.settings?.designTheme || 'modern',
            colorTheme: getStore.settings?.colorTheme || '#B3D946',
            imgs: getStore.settings?.imgs || [],
            slidesEnabled: getStore.settings?.slidesEnabled !== false,
            slidesHeight: getStore.settings?.slidesHeight || 200,
         },
      };

      setValues(newValues);
      // Update initial values when store changes
      initialValuesRef.current = newValues;
   }, [values.store, storesResponse]);

   useEffect(() => {
      if (!initialValuesRef.current) return;

      const hasChanges =
         JSON.stringify(values) !== JSON.stringify(initialValuesRef.current);
      handleShowSave(hasChanges);

      // Only update store values if there are changes
      if (hasChanges) {
         handleValues(values, 'customization');
      }

      // Cleanup function
      return () => {
         handleValues(null, null);
      };
   }, [values, handleShowSave, handleValues]);

   const currentStore = storesResponse?.result?.find(
      (s: TableStore) => s._id === values.store
   );

   const stores = storesResponse?.result?.map((store: TableStore) => ({
      _id: store._id,
      storename: store.storename,
      storeDomain: store.storeDomain,
      settings: store.settings,
   }));

   const { store, settings } = values;

   const handleDesignThemeChange = (theme: string) => {
      setValues({ ...values, settings: { ...settings, designTheme: theme } });
   };

   const colorsStyle = [
      '#B3D946',
      '#627AFF',
      '#FA7A7A',
      '#939393',
      '#FFB325',
      '#222222',
   ];

   useEffect(() => {
      if (successSave) {
         handleRefresh();
         initialValuesRef.current = values;
      }
   }, [successSave]);

   useEffect(() => {
      handleValues(values, 'customization');

      return () => {
         handleValues(null, null);
      };
   }, [values]);

   const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUploading) return;
      setIsUploading(true);
      setUploadProgress(0);

      const files = e.target.files;
      if (!files) return;

      // Check if adding new images would exceed the limit
      const currentImageCount = values.settings.imgs.length;
      const newImageCount = files.length;
      const imgLimit = 10; // Match the server-side limit

      if (currentImageCount + newImageCount > imgLimit) {
         toast.error(
            `Cannot upload more than ${imgLimit} images. You currently have ${currentImageCount} images.`,
            {
               position: 'bottom-center',
               autoClose: 5000,
               hideProgressBar: false,
               closeOnClick: true,
               pauseOnHover: true,
            }
         );
         setIsUploading(false);
         setUploadProgress(0);
         return;
      }

      const uploadPromises = Array.from(files).map((file, index) => {
         return new Promise<string>((resolve, reject) => {
            // Check file size (10MB limit)
            if (file.size > 10 * 1024 * 1024) {
               reject(
                  new Error(
                     `File ${file.name} is too large. Maximum size is 10MB.`
                  )
               );
               return;
            }

            // Use chunked upload for files larger than 1MB, base64 for smaller files
            if (file.size > 1024 * 1024) {
               // Use file upload (chunked) for larger files
               uploadImageSlider(file, (percent) => {
                  setUploadProgress(percent < 99 ? percent : 99);
               })
                  .then((response) => {
                     setUploadProgress(100); // Only set to 100% after upload is confirmed
                     setTimeout(() => {
                        setIsUploading(false);
                        setUploadProgress(0);
                     }, 500); // Hide spinner after short delay
                     resolve(response.data.imageUrl);
                  })
                  .catch((error) => {
                     const apiError = error as ApiError;
                     if (apiError.response?.status === 400) {
                        reject(
                           new Error(
                              apiError.response.data.error || 'Upload failed'
                           )
                        );
                     } else {
                        reject(error);
                     }
                  });
            } else {
               // Use base64 for smaller files (faster for small images)
               const reader = new FileReader();

               reader.onload = async (e) => {
                  try {
                     const base64Image = e.target?.result as string;
                     const response = await uploadImageSlider(base64Image);
                     setUploadProgress(100);
                     setTimeout(() => {
                        setIsUploading(false);
                        setUploadProgress(0);
                     }, 500);
                     resolve(response.data.imageUrl);
                  } catch (error: unknown) {
                     const apiError = error as ApiError;
                     if (apiError.response?.status === 400) {
                        reject(
                           new Error(
                              apiError.response.data.error || 'Upload failed'
                           )
                        );
                     } else {
                        reject(error);
                     }
                  }
               };

               reader.onerror = () => reject(new Error('Failed to read file'));
               reader.readAsDataURL(file);
            }
         });
      });

      try {
         const imageUrls = await Promise.all(uploadPromises);

         // Update local state
         setValues({
            ...values,
            settings: {
               ...values.settings,
               imgs: [...values.settings.imgs, ...imageUrls],
            },
         });

         toast.success('Images uploaded successfully', {
            position: 'bottom-center',
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
         });

         // setIsUploading(false); // Now handled in the upload promise
         // setUploadProgress(0); // Now handled in the upload promise
      } catch (error: unknown) {
         const apiError = error as ApiError;
         console.error('Error uploading images:', apiError);
         toast.error(apiError.message || 'Error uploading images', {
            position: 'bottom-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
         });
         setIsUploading(false);
         setUploadProgress(0);
      }
   };

   const handleDeleteImage = (index: number) => {
      setValues({
         ...values,
         settings: {
            ...settings,
            imgs: values.settings.imgs.filter((_, i) => i !== index),
         },
      });
   };

   const handleSelect = (item: SelectOption) => {
      setValues({ ...values, store: item._id });
   };

   return (
      <Wrapper>
         <PageTitle title="Customization" actions={[]} />
         <Content>
            <Main>
               <ContentTitle>Select Store</ContentTitle>
               <Select
                  title=""
                  // data={
                  //    stores?.map(
                  //       (item: { _id: string; storename: string }) => ({
                  //          _id: item._id,
                  //          name: item.storename,
                  //       })
                  //    ) || []
                  // }
                  data={storeData}
                  onChange={(item) => handleSelect(item)}
                  srcIconDefault="store-3-line"
               />

               <SlidesSettingsWrapper>
                  <SlidesToggleRow>
                     <ContentTitle>Image Slides</ContentTitle>
                     <SlidesToggle
                        $active={settings.slidesEnabled}
                        onClick={() =>
                           setValues({
                              ...values,
                              settings: { ...settings, slidesEnabled: !settings.slidesEnabled },
                           })
                        }
                     >
                        <SlidesToggleKnob $active={settings.slidesEnabled} />
                     </SlidesToggle>
                  </SlidesToggleRow>
                  {settings.slidesEnabled && (
                     <SlidesHeightRow>
                        <SlidesHeightLabel>Slide Height: {settings.slidesHeight}px</SlidesHeightLabel>
                        <SlidesHeightSlider
                           type="range"
                           min={120}
                           max={400}
                           step={10}
                           value={settings.slidesHeight}
                           onChange={(e) =>
                              setValues({
                                 ...values,
                                 settings: { ...settings, slidesHeight: Number(e.target.value) },
                              })
                           }
                        />
                     </SlidesHeightRow>
                  )}
               </SlidesSettingsWrapper>

               {settings.slidesEnabled && (
               <SliderWrapper>
                  <ContentTitle>
                     Slider {values.settings.imgs.length}/10
                     {/* <ImageLimit>
                        {values.settings.imgs.length}/5 images
                     </ImageLimit> */}
                  </ContentTitle>
                  <List>
                     {(values.settings.imgs as string[]).map(
                        (img: string, index: number) => (
                           <Item key={index}>
                              <CloseBtn
                                 onClick={() => handleDeleteImage(index)}
                              >
                                 <IconsLib
                                    icon="close-line"
                                    color={colors.GRAY4}
                                    size="20px"
                                 />
                              </CloseBtn>

                              <ItemImgWrapper>
                                 <ItemImg src={img} alt={`slide-${index}`} />
                              </ItemImgWrapper>
                           </Item>
                        )
                     )}

                     <>
                        {values.settings.imgs.length < 10 && (
                           <>
                              {isUploading ? (
                                 <ItemUpload
                                    onClick={() => {
                                       const fileInput = document.querySelector(
                                          'input[type="file"]'
                                       ) as HTMLInputElement;
                                       if (fileInput) fileInput.click();
                                    }}
                                 >
                                    <IconsLib
                                       icon="loader"
                                       color={colors.BLUE_LIGHT}
                                       size="30px"
                                    />
                                    <BtnLabel>
                                       Uploading... {Math.round(uploadProgress)}
                                       %
                                    </BtnLabel>
                                 </ItemUpload>
                              ) : (
                                 <ItemUpload
                                    onClick={() => {
                                       const fileInput = document.querySelector(
                                          'input[type="file"]'
                                       ) as HTMLInputElement;
                                       if (fileInput) fileInput.click();
                                    }}
                                 >
                                    <UploadInput
                                       type="file"
                                       onChange={handleUploadImage}
                                       accept="image/*"
                                       multiple={true}
                                       style={{ display: 'none' }}
                                    />
                                    <IconsLib
                                       icon="upload-cloud-line"
                                       color={colors.BLUE_LIGHT}
                                       size="30px"
                                    />
                                    <BtnLabel>Upload Image </BtnLabel>
                                 </ItemUpload>
                              )}
                           </>
                        )}
                     </>
                  </List>
               </SliderWrapper>
               )}

               <DesignTheme>
                  <ContentTitle>Design Theme</ContentTitle>
                  <DesignThemeList>
                     {[
                        {
                           id: 'basic',
                           label: 'Basic',
                           desc: 'Clean and simple',
                           bg: '#ffffff',
                           textColor: '#333',
                           accent: '#6c757d',
                           items: ['#f5f5f5', '#f5f5f5'],
                        },
                        {
                           id: 'modern',
                           label: 'Modern',
                           desc: 'Sleek card grid',
                           bg: '#f8fafc',
                           textColor: '#0f172a',
                           accent: '#3760F9',
                           items: ['#eef2ff', '#eef2ff'],
                        },
                        {
                           id: 'dark',
                           label: 'Dark',
                           desc: 'Easy on the eyes',
                           bg: '#0f0f17',
                           textColor: '#e2e8f0',
                           accent: '#a78bfa',
                           items: ['#1a1a2e', '#1a1a2e'],
                        },
                        {
                           id: 'gaming',
                           label: 'Gaming',
                           desc: 'Neon cyberpunk',
                           bg: '#0a0a0f',
                           textColor: '#00e676',
                           accent: '#00e676',
                           items: ['#141420', '#141420'],
                        },
                        {
                           id: 'shopee',
                           label: 'Shopee',
                           desc: 'E-commerce style',
                           bg: '#fff5f2',
                           textColor: '#222',
                           accent: '#ee4d2d',
                           items: ['#ffffff', '#ffffff'],
                        },
                        {
                           id: 'professional',
                           label: 'Professional',
                           desc: 'Corporate & clean',
                           bg: '#f0f4f8',
                           textColor: '#0f172a',
                           accent: '#0f172a',
                           items: ['#ffffff', '#ffffff'],
                        },
                     ].map((theme) => (
                        <ThemeCard
                           key={theme.id}
                           $checked={settings.designTheme === theme.id}
                           onClick={() => handleDesignThemeChange(theme.id)}
                        >
                           <ThemePreview $bg={theme.bg}>
                              <ThemePreviewHeader>
                                 <ThemePreviewDot $color={theme.accent} />
                                 <ThemePreviewTitle $color={theme.textColor}>Store</ThemePreviewTitle>
                              </ThemePreviewHeader>
                              <ThemePreviewImgPlaceholder $color={theme.accent} />
                              <ThemePreviewItem $bg={theme.items[0]} $accent={theme.accent}>
                                 <ThemePreviewItemText $color={theme.textColor} />
                                 <ThemePreviewItemBtn $color={theme.accent} />
                              </ThemePreviewItem>
                              <ThemePreviewItem $bg={theme.items[1]} $accent={theme.accent}>
                                 <ThemePreviewItemText $color={theme.textColor} />
                                 <ThemePreviewItemBtn $color={theme.accent} />
                              </ThemePreviewItem>
                           </ThemePreview>
                           <ThemeCardFooter>
                              <ThemeCardLabel>{theme.label}</ThemeCardLabel>
                              <ThemeCardDesc>{theme.desc}</ThemeCardDesc>
                           </ThemeCardFooter>
                           {settings.designTheme === theme.id && (
                              <ThemeCheckMark>
                                 <IconsLib
                                    icon="checkbox-circle-fill"
                                    size="20px"
                                    color={colors.BLUE_LIGHT}
                                 />
                              </ThemeCheckMark>
                           )}
                        </ThemeCard>
                     ))}
                  </DesignThemeList>
               </DesignTheme>
            </Main>
            <Theme>
               <PreviewTitle>Selected Theme</PreviewTitle>
               <ShopTheme
                  designTheme={settings.designTheme}
                  slidesEnabled={settings.slidesEnabled}
                  slidesHeight={settings.slidesHeight}
                  storeName={values.storename}
                  storeLocation={values.location}
                  storeLogo={currentStore?.logo}
               />
               <PreviewLiveLink
                  href={values.storeDomain} // remove the https://
                  target="_blank"
               >
                  Live Preview
               </PreviewLiveLink>
               {/* <button onClick={handleSubmit}>Submit</button> */}
            </Theme>
         </Content>
      </Wrapper>
   );
};
const Wrapper = styled.div``;
const Content = styled.div`
   display: grid;
   grid-template-columns: 1fr 250px;
   gap: 1rem;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;
const Main = styled.div`
   border: 1px solid ${colors.GRAY13};
   padding: 1rem;
   border-radius: 10px;
`;
const ContentTitle = styled.p`
   font-size: 16px;
   font-weight: 600;
   text-align: center;
   display: flex;
   align-items: center;
   justify-content: space-between;
`;
const PreviewTitle = styled.p`
   font-size: 16px;
   font-weight: 600;
   text-align: center;
`;
const Theme = styled.div`
   /* display: grid;
   place-items: center; */
   text-align: center;
   @media (max-width: 768px) {
      display: none;
   }
`;
const SliderWrapper = styled.div`
   margin-top: 1rem;
`;
const List = styled.div`
   margin-top: 1rem;
   display: flex;
   flex-wrap: wrap;
   gap: 1rem;
`;
const Item = styled.div`
   position: relative;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   /* padding: 5px; */
`;
const ItemTheme = styled.div`
   text-align: center;
`;
const ItemImg = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;
const ItemImgWrapper = styled.div`
   background-color: ${colors.GRAY13};
   width: 110px;
   height: 110px;
   object-fit: cover;
   border-radius: 10px;
   overflow: hidden;

   @media (max-width: 768px) {
      width: 80px;
      height: 80px;
   }
`;

const CloseBtn = styled.div`
   position: absolute;
   top: 7px;
   right: 7px;
   border-radius: 50%;
   background-color: ${colors.GRAY13};
   cursor: pointer;
`;
const ItemUpload = styled.div`
   display: grid;
   place-items: center;

   gap: 0.5rem;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   padding: 1rem;
   height: 110px;
   width: 110px;
   cursor: pointer;

   @media (max-width: 768px) {
      height: 110px;
      width: 100%;
   }
`;
const BtnLabel = styled.p`
   font-size: 14px;
   color: ${colors.GRAY4};
   font-weight: 500;
   text-align: center;
`;

const ColorSwatchesWrapper = styled.div`
   margin-top: 1rem;
   display: flex;
   flex-wrap: wrap;
   gap: 0.5rem;
`;

const ColorSwatchItem = styled.div<{ $color: string }>`
   width: 40px;
   height: 40px;
   border-radius: 4px;
   background-color: ${({ $color }) => $color};
   border: 1px solid ${colors.GRAY13};
   cursor: pointer;
`;
const ColorTheme = styled.div`
   margin-top: 2rem;
`;

const SlidesSettingsWrapper = styled.div`
   margin-top: 1.5rem;
   padding: 12px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;

const SlidesToggleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
`;

const SlidesToggle = styled.div<{ $active: boolean }>`
   width: 40px;
   height: 22px;
   border-radius: 11px;
   background: ${({ $active }) => ($active ? colors.SUCCESS : colors.GRAY10)};
   cursor: pointer;
   position: relative;
   transition: background 0.2s;
`;

const SlidesToggleKnob = styled.div<{ $active: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 50%;
   background: var(--app-surface);
   position: absolute;
   top: 2px;
   left: ${({ $active }) => ($active ? '20px' : '2px')};
   transition: left 0.2s;
   box-shadow: 0 1px 3px rgba(0,0,0,0.15);
`;

const SlidesHeightRow = styled.div`
   margin-top: 12px;
`;

const SlidesHeightLabel = styled.p`
   font-size: 13px;
   color: ${colors.GRAY4};
   margin-bottom: 6px;
`;

const SlidesHeightSlider = styled.input`
   width: 100%;
   accent-color: ${colors.PRIMARY};
`;

const DesignTheme = styled.div`
   margin-top: 2rem;
`;
const DesignThemeList = styled.div`
   margin-top: 1rem;
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;
   @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
   }
   @media (max-width: 400px) {
      grid-template-columns: 1fr;
   }
`;

const ThemeCard = styled.div<{ $checked: boolean }>`
   position: relative;
   border: 2px solid ${({ $checked }) => ($checked ? colors.BLUE_LIGHT : colors.GRAY13)};
   border-radius: 10px;
   overflow: hidden;
   cursor: pointer;
   transition: all 0.2s;
   background: var(--app-surface);

   &:hover {
      border-color: ${({ $checked }) => ($checked ? colors.BLUE_LIGHT : colors.GRAY10)};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
   }
`;

const ThemePreview = styled.div<{ $bg: string }>`
   background: ${({ $bg }) => $bg};
   padding: 10px 8px 6px;
   display: flex;
   flex-direction: column;
   gap: 4px;
   height: 110px;
   border-radius: 8px 8px 0 0;
`;

const ThemePreviewHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   margin-bottom: 2px;
`;

const ThemePreviewDot = styled.div<{ $color: string }>`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: ${({ $color }) => $color};
`;

const ThemePreviewTitle = styled.span<{ $color: string }>`
   font-size: 8px;
   font-weight: 700;
   color: ${({ $color }) => $color};
`;

const ThemePreviewImgPlaceholder = styled.div<{ $color: string }>`
   height: 22px;
   border-radius: 4px;
   background: ${({ $color }) => `${$color}18`};
   border: 1px dashed ${({ $color }) => `${$color}40`};
`;

const ThemePreviewItem = styled.div<{ $bg: string; $accent: string }>`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 5px 6px;
   border-radius: 4px;
   background: ${({ $bg }) => $bg};
   border: 1px solid ${({ $accent }) => `${$accent}20`};
`;

const ThemePreviewItemText = styled.div<{ $color: string }>`
   width: 45%;
   height: 5px;
   border-radius: 2px;
   background: ${({ $color }) => `${$color}30`};
`;

const ThemePreviewItemBtn = styled.div<{ $color: string }>`
   width: 28%;
   height: 8px;
   border-radius: 3px;
   background: ${({ $color }) => $color};
`;

const ThemeCardFooter = styled.div`
   padding: 8px 10px;
   border-top: 1px solid ${colors.GRAY13};
`;

const ThemeCardLabel = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY_DARK};
`;

const ThemeCardDesc = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 1px;
`;

const ThemeCheckMark = styled.div`
   position: absolute;
   top: 6px;
   right: 6px;
`;

const UploadInput = styled.input``;

const ImageLimit = styled.span`
   font-size: 14px;
   color: ${colors.GRAY4};
   font-weight: normal;
`;

const PreviewLiveLink = styled.a`
   font-size: 14px;
   color: ${colors.BLUE_LIGHT};
   font-weight: normal;
   text-decoration: underline;
   cursor: pointer;
`;

export default Customization;
